import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import { 
    Stack,
    FormGroup,
    FormControlLabel,
    Typography,
    Grid,
    Checkbox,
    Card,
    useTheme,
    Box,
    LinearProgress,
    Paper
} from "@mui/material";
import { TabPanel, TabContext } from "@mui/lab";

import DXFParser from 'dxf-parser';

import StyledButton from "../../../styled/Button";
import StyledTextField from "../../../styled/TextField";
import StyledTab from "../../../styled/Tab";
import StyledTabs from "../../../styled/Tabs";

import apiClient from "../../../../services/api";

import store from "../../../../store/index";
import { toggleMessageOpen } from "../../../../store/actions/Message";
import { createConfirmAction } from "../../../../store/actions/ConfirmDialog";

import { LoadingScreen } from "../../../partials/LoadingScreen";

import { ElementsTable } from "../map/SelectFloorPlanLayer";

const FormTitle = ({ type }) => {
    return (
        <Box paddingBottom={2}>
            <Typography variant='h2'>
                {type === 'create' && (<Trans>Add new Floor plan</Trans>)}
                {type === 'edit' && (<Trans>Edit Floor plan</Trans>)}
            </Typography>
        </Box>
    );
};

const FloorPlanForm = (props) => {
    return (
        <Fragment>
            <FormTitle type={props.type} />
                <Typography variant="caption">
                    <Trans>Floor Plan Name</Trans>
                </Typography>
                <StyledTextField
                    margin="dense"
                    id="number"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={props.name}
                    size="small"
                    onChange={props.handleNameChange}
                    error={props.errors.name}
                    helperText={props.errors.name}
                />
                <FormGroup>
                    <FormControlLabel
                        label={props.t('Multiple versions enabled')}
                        control={
                            <Checkbox
                                checked={props.multipleVersionsEnabled}
                                onChange={props.handleMultipleVersionsEnabledChange}
                            />
                        }
                    />
                </FormGroup>
            <FloorPlanTypeTabs
                width={props.width}
                depth={props.depth}
                path={props.path}
                setPath={props.setPath}
                setWidth={props.setWidth}
                setDepth={props.setDepth}
                handleWidthChange={props.handleWidthChange}
                handleDepthChange={props.handleDepthChange}
                selectedTab={props.selectedTab}
                setSelectedTab={props.setSelectedTab}
                selectedLayer={props.selectedLayer}
                setSelectedLayer={props.setSelectedLayer}
                parseFile={props.parseFile}
                layers={props.layers}
                setLayers={props.setLayers}
                errors={props.errors}
            />
            <Buttons handleSave={props.handleSave} handleDelete={props.handleDelete} type={props.type} selectedTab={props.selectedTab} />
        </Fragment>
    )
}

const Manual = (props) => {
    return (
        <Grid container spacing={2}>
            <Grid item xs={6}>
                <StyledTextField
                    fullWidth
                    value={props.width}
                    onChange={props.handleWidthChange}
                    size="small"
                    variant='outlined'
                    label={props.t('Width(m)')}
                    error={props.errors.width}
                    helperText={props.errors.width}
                />
            </Grid>
            <Grid item xs={6}>
                <StyledTextField
                    fullWidth
                    value={props.depth}
                    onChange={props.handleDepthChange}
                    size="small"
                    variant='outlined'
                    label={props.t('Depth(m)')}
                    error={props.errors.depth}
                    helperText={props.errors.depth}
                />
            </Grid>
        </Grid>
    )
}

const DXF = (props) => {
    function LinearProgressWithLabel({uploadStarted}) {
        return (
            <Box
                sx={{
                    paddingTop: "15px",
                    display: props.uploadStarted ? "flex" : "none",
                    alignItems: "center"
                }}
            >
                <Box sx={{ width: "100%", mr: 1 }}>
                    <LinearProgress />
                    <Box alignContent="center"><Trans>Uploading file..</Trans></Box>
                </Box>
            </Box>
        )
    }

    const getFileName = (url) => {
        if(!url) {
            return '';
        }

        const data = url.split('/');

        return data.slice(-1);
    }

    return (
        <Fragment>
            <LinearProgressWithLabel uploadStarted={props.uploadStarted} />
            <div>
                <div style={{display: props.path ? 'inline-block' : 'none', marginRight: '5px'}}>
                    <span style={{fontWeight: 'bold'}}><Trans>Uploaded file: </Trans></span>{getFileName(props.path)}
                </div>
                <div style={{display: props.path ? 'inline-block' : 'none'}}>
                    <span style={{fontWeight: 'bold'}}><Trans>Selected Layer: </Trans></span>{props.selectedLayer}
                </div>
            </div>
            <StyledButton
                onClick={props.openFileUpload}
                variant="contained"
                style={{
                    marginBottom: '5px',
                    display: props.uploadStarted && !props.path ? 'none' : 'inline-block'
                }}
            >
                <Trans>{!props.path ? 'Upload' : 'Change'}</Trans>
            </StyledButton>
            {
                props.path
                    ?   <StyledButton
                            onClick={props.onFileRemove}
                            variant="contained"
                            style={{
                                marginBottom: '5px',
                                marginLeft: '5px',
                                display: props.uploadStarted && !props.path ? 'none' : 'inline-block'
                            }}
                        >
                            <Trans>Remove DXF</Trans>
                        </StyledButton>
                    : null
            }
            <input type="file" id="dxfFileUploadInput" onChange={props.onSaveDxfFile} style={{ display: "none" }} />
            <Layers layers={props.layers} selected={props.selectedLayer} setSelected={props.setSelectedLayer} />
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <StyledTextField
                        value={props.width}
                        fullWidth
                        size="small"
                        variant='outlined'
                        disabled={true}
                        label={props.t('Width(m)')}
                    />
                </Grid>
                <Grid item xs={6}>
                    <StyledTextField
                        value={props.depth}
                        fullWidth
                        size="small"
                        variant='outlined'
                        disabled={true}
                        label={props.t('Depth(m)')}
                    />
                </Grid>
            </Grid>
        </Fragment>
    )
}

const Layers = ({layers, selected, setSelected}) => {
    if(!layers || layers.length === 0) {
        return;
    }

    return (
        <div style={{maxHeight: '350px', marginBottom: '10px', overflow: 'hidden'}}>
            <ElementsTable rows={layers} selected={selected} setSelected={setSelected} />
        </div>
    );
}

const FloorPlanTypeTabs = (props) => {
    const { t } = useTranslation();

    const [uploadStarted, setUploadStarted] = useState(false);

    const theme = useTheme();

    const handleChangeTab = (event, newTab) => { props.setSelectedTab(newTab) };
    
    const openFileUpload = () => {
        const fileInput = document.getElementById('dxfFileUploadInput');

        if(fileInput) {
            fileInput.click();
        }
    }

    const onFileRemove = () => {
        props.setPath(null);
        props.setWidth(0);
        props.setDepth(0);
        props.setLayers(0);
    }

    const clearFileUpload = () => {
        const fileInput = document.getElementById('dxfFileUploadInput');

        if (fileInput) {
            fileInput.value = '';
        }
    }

    const onSaveDxfFile = (event) => {
        if (!event.target.files || event.target.files.length < 1) {
            return store.dispatch(toggleMessageOpen("Upload failed, no file found to upload!", "error"));
        }
    
        setUploadStarted(true);

        const formData = new FormData();

        formData.append("tmp", true);
        formData.append("file", event.target.files[0]);

        apiClient.post("/api/easy-floor/upload-file", formData).then(response => {
            setUploadStarted(false);

            if(response.data.success) {
                props.setPath(response.data.path);
                props.parseFile(response.data.path);
            } else {
                store.dispatch(toggleMessageOpen(response.data.message, 'error'));
            }

            clearFileUpload();
        })
        .catch(error => {
            setUploadStarted(false);
            clearFileUpload();

            store.dispatch(toggleMessageOpen(error.response.data.message, "error"));
        });

        return true;
    }

    return (
        <TabContext value={props.selectedTab.toString()}>
            <Box style={{backgroundColor: theme.palette.storeDrawer.tabs.backgroundColor, borderRadius: '8px', height: '40px' }}>
                <StyledTabs
                    value={props.selectedTab}
                    onChange={handleChangeTab}
                    textColor="inherit"
                    variant="fullWidth"
                >
                    <StyledTab label={t('Manual')} />
                    <StyledTab label={t('DXF')} />
                </StyledTabs>
            </Box>
            
            <TabPanel value="0" style={{ height: '100%', padding: '0', paddingTop: '5px', overflow: 'hidden' }}>
                <Manual
                    width={props.width}
                    depth={props.depth}
                    handleDepthChange={props.handleDepthChange}
                    handleWidthChange={props.handleWidthChange}
                    errors={props.errors}
                    t={t}
                />
            </TabPanel>
        
            <TabPanel value="1" style={{ height: '100%', padding: '0', paddingTop: '5px', overflow: 'hidden' }}>
                <DXF
                    width={props.width}
                    depth={props.depth}
                    uploadStarted={uploadStarted}
                    path={props.path}
                    layers={props.layers}
                    selectedLayer={props.selectedLayer}
                    setSelectedLayer={props.setSelectedLayer}
                    onSaveDxfFile={onSaveDxfFile}
                    openFileUpload={openFileUpload}
                    onFileRemove={onFileRemove}
                    t={t}
                />
            </TabPanel>
        </TabContext>
    )
}

const Buttons = ({ handleSave, handleDelete, type, selectedTab}) => {
    const theme = useTheme();

    return (
        <Stack direction="row" spacing={2} style={{ marginTop: '10px' }}>
            <StyledButton
                variant="contained"
                onClick={() => handleSave(selectedTab != 0 ? 'DXF' : 'MANUAL')}
            >
                <Trans>Save</Trans>
            </StyledButton>
            {
                type === 'edit'
                    ?   <StyledButton
                            variant="contained"
                            onClick={() => store.dispatch(createConfirmAction(handleDelete, <Trans>Are you sure?</Trans>))}
                            style={{
                                backgroundColor: theme.palette.background.button,
                                color: theme.palette.white.main
                            }}
                        >
                            <Trans>Delete</Trans>
                        </StyledButton>
                    : null
            }
        </Stack>
    );
};

function StoreCreateContainer({ type }) {
    const { floorPlanId } = useParams();

    const theme = useTheme();

    const { t } = useTranslation();
    const navigate = useNavigate();

    const [floorPlans, setFloorPlans] = useState([]);
    const [errors, setErrors] = useState([]);

    const [name, setName] = useState('');
    const [width, setWidth] = useState(0);
    const [depth, setDepth] = useState(0);
    const [path, setPath] = useState(null);
    const [multipleVersionsEnabled, setMultipleVersionsEnabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedLayer, setSelectedLayer] = useState(null);
    const [layers, setLayers] = useState([]);
    const [dxfData, setDxfData] = useState(null);
    const [floorPlanLoading, setFloorPlanLoading] = useState(false);

    const handleNameChange = (event) => { setName(event.target.value) }
    const handleWidthChange = (event) => { setWidth(event.target.value) }
    const handleDepthChange = (event) => { setDepth(event.target.value) }
    const handleMultipleVersionsEnabledChange = (event) => { setMultipleVersionsEnabled(event.target.checked) }
    
    useEffect(() => {
        setLoading(true);

        apiClient.post('/api/modules/check', {name: 'easyFloor'}).then(response => {
            if(!response.data.success) {
                store.dispatch(toggleMessageOpen(t("You don't have permission to enter this page"), "error"))

                navigate('/app/home');
            } else {
                setLoading(false)
            }
        })
    }, [])// eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        onInit();

        if(!!floorPlanId && type === 'edit') {
            setFloorPlanLoading(true);
            apiClient.post('/api/easy-floor/get', { id: floorPlanId }).then(response => {
                if(response.data.success) {
                    onFloorPlanLoaded(response.data);
                }
            }).catch(error => {
                //store.dispatch(toggleMessageOpen(error.response.data, "error"))
            });
        }
    }, [floorPlanId, type]);

    useEffect(() => {
        if(!selectedLayer || !dxfData) {
            return;
        }

        calculateDimensions();
    }, [selectedLayer, dxfData])
    
    const parseFile = (url) => {
        fetch(url)
            .then((response) => response.blob())
            .then((data) => {
                const reader = new FileReader();

                reader.onload = (e) => {
                    const dxfData = e.target.result;
                    const parser = new DXFParser();
                    const parsedData = parser.parseSync(dxfData);
                    const layersObject = parsedData.tables.layer.layers;

                    setLayers(Object.values(layersObject));
                    setDxfData(parsedData);
                };

                reader.readAsText(data);
        })
        .catch((error) => {
            console.error('Error fetching or parsing DXF file:', error);
        });
    }

    const calculateDimensions = () => {
        if(!dxfData) {
            return;
        }
        
        let minX, minY, maxX, maxY = undefined;

        dxfData.entities.forEach((entity) => {
            if(entity.layer === selectedLayer) {
                if(entity.type !== 'LWPOLYLINE')
                {
                    return;
                }

                if(entity.vertices) {
                    entity.vertices.forEach((vertex) => {
                        if(!minX && !minY && !maxX && !maxY) {
                            minX = vertex.x;
                            minY = vertex.y;
                            maxX = vertex.x;
                            maxY = vertex.y;
                        }
                        
                        if(vertex.x < minX) {
                            minX = vertex.x;
                        }
                        
                        if(vertex.y < minY) {
                            minY = vertex.y;
                        }
                        
                        if(vertex.x > maxX) {
                            maxX = vertex.x;
                        }
                        
                        if(vertex.y > maxY) {
                            maxY = vertex.y;
                        }
                    })
                }
            }
        })

        setWidth(Number((maxX - minX) / 100).toFixed(2));
        setDepth(Number((maxY - minY) / 100).toFixed(2));
    }

    const onInit = () => {
        setName('');
        setPath('');
        setLayers([]);
        setSelectedLayer(null);
        setMultipleVersionsEnabled(true);
    }

    const onFloorPlanLoaded = (response) => {
        setName(response.floor_plan.name);
        setPath(response.dxf);
        setMultipleVersionsEnabled(response.floor_plan.multiple_versions_enabled === 1 ? true : false);

        if(response.dxf) {
            parseFile(response.dxf);
            setSelectedLayer(response.floor_plan.floor_plan_layer);
            setSelectedTab(1);
        } else {
            setWidth(response.floor_plan.width);
            setDepth(response.floor_plan.depth);
            setSelectedTab(0);
        }

        setFloorPlanLoading(false);
    }

    const handleSave = (type) => {
        const formData = getFormData(type);

        if(selectedTab != 0) {
            if(!path) {
                store.dispatch(toggleMessageOpen(t('DXF is required'), 'error'));

                return;
            }

            if(!selectedLayer) {
                store.dispatch(toggleMessageOpen(t('Base layer is required'), 'error'));

                return;
            }
        }

        apiClient.post('/api/easy-floor/save', formData).then(response => {
            if(response.data?.success) {
                store.dispatch(toggleMessageOpen(t('Store saved successfully'), "success"));
                if (response.data?.floorPlanID) {
                    navigate('/app/easy-floor/map/' + response.data.floorPlanID);
                }
            }
        }).catch(error => {
            setErrors(error.response?.data)
        });
    }
    
    const handleDelete = () => {
        apiClient.post('/api/easy-floor/delete', {id: floorPlanId}).then( response => {
            if(response.data.success) {
                store.dispatch(toggleMessageOpen(t('Store deleted successfully'), 'success'))
    
                navigate('/app/easy-floor');
            } else {
                store.dispatch(toggleMessageOpen(response.data.message, 'error'))
            }

        }).catch( error => store.dispatch(toggleMessageOpen(error.response.data, "error")) )
    }

    const getFormData = (type) => {
        const formData = new FormData();

        if(floorPlanId) {
            formData.append('id', floorPlanId);
        }

        if(type == 'DXF') {
            formData.append('dxf', path);
            formData.append('floor_plan_layer', selectedLayer);
        }

        formData.append('type', type);
        formData.append('name', name);
        formData.append('width', width);
        formData.append('depth', depth);
        formData.append('multiple_versions_enabled', multipleVersionsEnabled ? 1 : 0);

        return formData;
    }

    const renderData = () => {
        return (
            <Paper style={{margin: '24px'}}>
                <Card 
                    sx={{
                        padding: 3,
                        minHeight: '80%',
                        minWidth: 'min(960px, 100%)',
                        borderRadius: 2,
                        backgroundColor: theme.palette.background.secondary,
                        position: 'relative'
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            width: 'calc(100% - 48px)',
                            height: 'calc(100% - 48px)',
                            backgroundColor: '#27324D',
                            zIndex: 9,
                            opacity: 0.9,
                            display: floorPlanLoading ? 'flex' : 'none',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                            color: '#40B2E0',
                        }}
                    >
                        <LoadingScreen />
                    </div>
                    <FloorPlanForm
                        type={type}
                        floorPlans={floorPlans}
                        name={name}
                        width={width}
                        depth={depth}
                        path={path}
                        selectedTab={selectedTab}
                        selectedLayer={selectedLayer}
                        layers={layers}
                        setLayers={setLayers}
                        setSelectedLayer={setSelectedLayer}
                        setSelectedTab={setSelectedTab}
                        setPath={setPath}
                        setWidth={setWidth}
                        setDepth={setDepth}
                        multipleVersionsEnabled={multipleVersionsEnabled}
                        errors={errors}
                        handleNameChange={handleNameChange}
                        handleWidthChange={handleWidthChange}
                        handleDepthChange={handleDepthChange}
                        handleMultipleVersionsEnabledChange={handleMultipleVersionsEnabledChange}
                        handleSave={handleSave}
                        handleDelete={handleDelete}
                        parseFile={parseFile}
                        t={t}
                    />
                </Card>
            </Paper>
        )
    }

    return (
        !loading ? renderData() : <LoadingScreen />
    );
}

export default StoreCreateContainer;